import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { updateMetaTags, defaultMeta } from '../utils/metaTags';

const PrivacyPolicy = () => {
  useEffect(() => {
    updateMetaTags({
      title: 'Privacy Policy | UK Salary Toolkit',
      description: 'Privacy Policy for UK Salary Toolkit. Learn how we handle your data, cookies, and privacy when using our free UK tax calculator.',
      canonical: `${defaultMeta.siteUrl}/privacy-policy`,
    });
  }, []);

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <motion.section 
        className="text-center space-y-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900">Privacy Policy</h1>
        <p className="text-gray-600">Last updated: January 27, 2025</p>
      </motion.section>

      <motion.div 
        className="bg-white rounded-xl p-6 md:p-8 shadow-sm border space-y-6 text-gray-700"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
          <p className="mb-4">
            UK Salary Toolkit ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website and use our UK tax calculator tools.
          </p>
          <p>
            By using our website, you consent to the data practices described in this policy.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>
          <h3 className="text-xl font-medium text-gray-900 mb-2">2.1 Information You Provide</h3>
          <p className="mb-4">
            Our calculator tools operate entirely in your browser. We do not collect, store, or transmit any personal information or salary data you enter into our calculators. All calculations are performed locally on your device.
          </p>
          
          <h3 className="text-xl font-medium text-gray-900 mb-2">2.2 Automatically Collected Information</h3>
          <p className="mb-2">We may automatically collect certain information when you visit our website, including:</p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>IP address and general location data</li>
            <li>Browser type and version</li>
            <li>Device information (operating system, screen resolution)</li>
            <li>Pages visited and time spent on pages</li>
            <li>Referring website addresses</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Cookies and Tracking Technologies</h2>
          <p className="mb-4">
            We use cookies and similar tracking technologies to improve your experience and analyze website traffic. These include:
          </p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li><strong>Essential Cookies:</strong> Required for the website to function properly</li>
            <li><strong>Advertising Cookies:</strong> Used by third-party advertisers (including Google AdSense) to deliver relevant ads based on your prior visits to this website and other websites</li>
          </ul>
          <p className="mb-4">
            You can control or disable cookies through your browser settings. However, disabling cookies may affect website functionality and your experience. See Section 4 below for how to opt out of personalized advertising.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Third-Party Services and Advertising</h2>
          <h3 className="text-xl font-medium text-gray-900 mb-2">4.1 Google AdSense</h3>
          <p className="mb-4">
            We use Google AdSense to display advertisements on this site. Third-party vendors, including Google, use cookies to serve ads based on a user's prior visits to our website or other websites on the Internet. Google's use of advertising cookies enables it and its partners to serve ads to our users based on their visit to our site and/or other sites on the Internet.
          </p>
          <h3 className="text-xl font-medium text-gray-900 mb-2">4.2 How to Opt Out of Personalized Advertising</h3>
          <p className="mb-2">
            You may opt out of personalized advertising in the following ways:
          </p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li><strong>Google Ads Settings:</strong> Visit <a href="https://www.google.com/settings/ads" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">Google's Ad Settings</a> to control how Google uses your data for ad personalization.</li>
            <li><strong>Industry opt-out (US and other regions):</strong> You can opt out of a third-party vendor's use of cookies for personalized advertising by visiting <a href="https://www.aboutads.info/choices/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">www.aboutads.info/choices</a>.</li>
            <li><strong>Browser settings:</strong> You can block or delete cookies via your browser settings. This may reduce the relevance of ads you see but does not necessarily stop all advertising.</li>
          </ul>
          <p className="mb-4">
            Other third-party vendors or ad networks may also serve ads on our site. You may visit those vendors' websites to opt out of the use of cookies for personalized advertising where they offer that option, or use the industry opt-out tool at <a href="https://www.aboutads.info/choices/" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">www.aboutads.info/choices</a>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. How We Use Your Information</h2>
          <p className="mb-2">We use collected information to:</p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Provide and improve our calculator tools</li>
            <li>Display relevant advertisements</li>
            <li>Ensure website security and prevent fraud</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
          <p className="mb-4">
            We implement appropriate technical and organizational measures to protect your information. However, no method of transmission over the Internet is 100% secure. While we strive to protect your data, we cannot guarantee absolute security.
          </p>
          <p>
            <strong>Important:</strong> All calculations are performed locally in your browser. We do not store or transmit any salary or personal financial information you enter into our calculators.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Your Rights (GDPR)</h2>
          <p className="mb-2">If you are located in the UK or EU, you have the right to:</p>
          <ul className="list-disc list-inside space-y-1 mb-4 ml-4">
            <li>Access your personal data</li>
            <li>Rectify inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to processing of your data</li>
            <li>Data portability</li>
            <li>Withdraw consent at any time</li>
          </ul>
          <p>
            To exercise these rights, please contact us through our <a href="/contact" className="text-blue-600 hover:underline">contact form</a>.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
          <p>
            Our website is not intended for children under 13 years of age. We do not knowingly collect personal information from children.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page and updating the "Last updated" date. You are advised to review this policy periodically.
          </p>
        </section>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;

